import { ListPlus, Percent } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
} from '@/components/ui/Card'
import { Field, Input } from '@/components/ui/Field'
import { cn } from '@/lib/cn'
import { useDocumentTemplates } from '@/features/directories/api/useDirectories'
import { useAddCostLines, useApplyMarkup } from '@/features/quotes/api/useQuote'
import { formatNumber } from '@/lib/format'
import { describeProblem, fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { QuoteDetail } from '@/types/domain'

/* Pricing a costing from its cost side (FR-1), the way the source workbook does.
 *
 * ── The two percentages, and why they are not one ────────────────────────────
 *
 * The workbook's "Profit Margin 0.05" is a MARKUP OVER COST: 9 600 × 1.05 =
 * 10 080. The figure the quote footer shows is a MARGIN OVER SELL, which on the
 * same costing reads 4.76%. They are different numbers describing the same
 * deal, and a panel that printed either of them under the other's name would
 * look like a form that cannot count. So both are on screen, each under its own
 * label: the markup is what the reader TYPES, the margin is what the server
 * answers with.
 *
 * ── Nothing here does arithmetic ─────────────────────────────────────────────
 *
 * Not one figure below is computed in this file. The cost, the customer total
 * and the margin all come off `record.costTotals`, which the server recomputes
 * on every read — because the margin a salesperson prices against must not
 * depend on which screen they read it from, and two surfaces that each add up
 * the same lines will eventually disagree by a rounding rule.
 *
 * The markup goes up as a STRING for the same reason the money does (AD-9): a
 * JSON number arrives as a float64 and prices a costing at 33.329999999999998%.
 *
 * ── The markup is STORED (S-5) ───────────────────────────────────────────────
 *
 * One markup per quote, held on the quote (`QuoteDetail.markupPercent`) and set
 * only by Apply, which re-prices every line at it in one transaction. Every
 * later line write is priced at it by the server, so an edited line keeps its
 * markup — the Q-11 defect this panel used to warn about in a sentence is gone,
 * and so is the sentence. The field opens on the stored figure; on a quote that
 * is no longer a draft the panel only states it.
 *
 * Apply is no longer refused on an empty costing or on a zero-cost one: a
 * stored markup set before the lines exist is a legitimate order, and pricing
 * zero-cost lines at it wipes nothing anybody typed — a line's price is derived
 * from its cost, never entered.
 *
 * ── Why the preset button lives here ─────────────────────────────────────────
 *
 * A costing starts from the same seven charge lines on nearly every quotation,
 * and typing them out is what the workbook's pre-written rows spared people. The
 * labels are maintained as `cost-item` templates in Settings, and this surface
 * reads them and posts them as WORDS. `internal/quote` may not query
 * `document_templates` (AD-1, AD-2) — the batch endpoint is generic and knows
 * nothing about templates, exactly as the not-included block picker's insertion
 * does not.
 *
 * They are inserted with no amounts. A rate stored on a template would be last
 * quarter's, read next quarter as though it were current; the money is entered
 * per quote, which is the whole reason a preset is a list of labels.
 */

/* The three markups an operator reaches for without thinking.
 *
 * Not configurable, and not read from Settings: they are a KEYBOARD SHORTCUT for
 * the field beside them, not a policy. A quote priced at 12.5% is typed, and
 * nothing here refuses it — a chip set that could be mistaken for the allowed
 * values would be a rule this product does not have.
 *
 * Strings, because that is what the field holds and what goes on the wire
 * (AD-9): a numeric 10 would have to be formatted back into the input, and the
 * formatting is where a locale's comma gets in. */
const MARKUP_PRESETS = ['10', '15', '20'] as const

/* A percentage the server will store (L7): digits, at most five before the
   point (it refuses 100000 and above) and at most four after it (the column
   keeps four places). A comma is allowed because that is what a Russian
   keyboard produces, and the mutation normalises it to a dot on the wire. */
const STORABLE_MARKUP = /^\d{1,5}([.,]\d{1,4})?$/

export function MarginPanel({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const markupMutation = useApplyMarkup(record.id)
  const presetMutation = useAddCostLines(record.id)
  const { data: templates } = useDocumentTemplates()

  /* What the reader has typed, held WITH the stored markup it was typed
     against; the field shows the quote's STORED markup otherwise (L18). Once
     the stored figure moves — this panel's own Apply landing, or another
     tab's — the typed figure was typed against a markup that no longer
     stands, and the field follows the record again: the held-edit rule
     `useHeaderAmend` applies to the header (L-6). Derived during render rather
     than reset in an effect, so the stale figure never shows for a frame. */
  const [typed, setTyped] = useState<{ value: string; over: string } | null>(null)
  const markup =
    typed && typed.over === record.markupPercent ? typed.value : record.markupPercent
  const type = (value: string) => setTyped({ value, over: record.markupPercent })

  const totals = record.costTotals
  const lines = record.costLines ?? []
  const preset = (templates ?? []).filter((template) => template.kind === 'cost-item')

  /* A quote that has gone out is read here, not priced (L18): the server
     refuses a markup write on anything but a draft (409), so the panel offers
     no field, no chips and no button, and states the stored markup instead. */
  const draft = record.status === 'draft'

  const markupUsable = STORABLE_MARKUP.test(markup.trim())

  /* Bare `disabled` on the PRESET button, which is the exception this
     codebase's Button documents rather than the rule: its cause is already on
     screen — the sentence beside it says Settings holds no standing charge
     lines. The Apply button's one refusal, a percentage the server cannot
     store, has no sentence on screen, so it goes through `blockedReason`. */
  const nothingToInsert = preset.length === 0

  /* L10. How many lines are not at the quote's markup — counted, not priced:
     the flag is the server's (`atQuoteMarkup`), and nothing here works out a
     figure from it. Said on a draft only, because the sentence is about what
     an edit and an Apply will do. */
  const offLines = lines.filter((line) => !line.atQuoteMarkup).length

  /* What a refused press says, and where it says it.
   *
   * The local condition on Apply — the percentage parses — is not what the
   * server is bound by: a quote sent from another tab answers 409, a stale
   * build answers 404, a role that may not re-price answers 403. With nothing
   * rendered off `isError`, all three arrive as a button that did nothing,
   * which is exactly how this reached us as "Apply markup changes nothing"
   * against a server path that was working correctly.
   *
   * TWO destinations, because they are two different problems for the reader. A
   * percentage the server rejected is a fault in the box they typed in, so it
   * goes BESIDE that box like every other field refusal in this system; anything
   * else is a failure of the ACT, which no edit to the field will fix, and
   * belongs above the row as a record-level message.
   *
   * The first code only. The server sends one problem per field here, and a
   * field that grew a second would be reporting two faults about one number —
   * which is a message to write when it exists rather than a list to render
   * speculatively. */
  const markupFieldCode = markupMutation.isError
    ? fieldErrors(markupMutation.error)['markupPercent']?.[0]
    : undefined
  /* Anything the field cannot hold. A validation refusal naming some OTHER
     field lands here too, deliberately: this form has one input, so a refusal
     about anything else is not something the reader can act on in it. */
  const markupFailed = markupMutation.isError && markupFieldCode === undefined

  const percent = (value: string | undefined) =>
    value === undefined ? t('common.empty') : `${formatNumber(value, 2)}%`

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <Percent size={15} className="text-text-muted" aria-hidden />
            {t('quotes.pricing.title')}
          </span>
        </CardTitle>
        {/* What the percentage below MEANS, moved off the field's hint and up
            here. It is a statement about the card rather than a note about the
            box — and with the hint gone the input, the chips and the button
            share one baseline instead of the button hanging below a line of
            small text. */}
        <CardSubtitle>{t('quotes.pricing.markupHint')}</CardSubtitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-4 pt-2">
        {/* The server's figures, read and never recomputed. Two are money and
            two are percentages, which is why neither percentage carries a
            currency: each is a ratio, not an amount.

            The markup sits BESIDE the margin (O6, L19): «Наценка» is what was
            set, «Маржа» what results, and the pair only reads correctly
            together. It is the costing's EFFECTIVE markup — the stored one
            while every line is at it, and an average, labelled so, when they
            differ.

            Shown unconditionally. QuoteAsideCards' money card prints the same
            figures in the detail page's right rail, but the rail is a READING
            surface, and this is the only place they stand beside the field and
            the button that change them. Both read `record.costTotals` and
            neither counts, so the repetition cannot turn into two answers. */}
        <dl className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-0.5">
            <dt className="asa-eyebrow">{t('quotes.pricing.cost')}</dt>
            <dd className="asa-num text-text-body text-[15px] leading-none">
              {formatNumber(totals?.buy.amount, 2)}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            {/* The COSTING's own word for this figure, not a second one of
                this panel's. `totals.sell` here and the figure under the heavy
                rule in CostLineEditor's footer are the same value off the same
                record, and the costing tab mounts both — so they are worded
                from one key, and a reword moves both together. */}
            <dt className="asa-eyebrow">{t('quotes.costLines.customerTotal')}</dt>
            <dd className="asa-num text-text-heading text-[15px] leading-none font-semibold">
              {formatNumber(totals?.sell.amount, 2)}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="asa-eyebrow">
              {totals?.markupMixed
                ? t('quotes.pricing.markupAverage')
                : t('quotes.pricing.markupEffective')}
            </dt>
            <dd className="asa-num text-text-body text-[15px] leading-none">
              {percent(totals?.markupPercent)}
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            {/* Named for WHAT IT IS rather than "Margin", because the markup
                beside it is a percentage too and the pair only reads correctly
                when each says which denominator it used. */}
            <dt className="asa-eyebrow">{t('quotes.pricing.marginOverRevenue')}</dt>
            <dd className="asa-num text-text-heading text-[15px] leading-none font-semibold">
              {percent(totals?.marginPercent)}
            </dd>
          </div>
        </dl>

        {draft ? (
          <>
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <Field
                label={t('quotes.pricing.markup')}
                error={markupFieldCode ? t(fieldErrorKey(markupFieldCode)) : undefined}
                className="w-full sm:w-48"
              >
                {(props) => (
                  <Input
                    inputMode="decimal"
                    value={markup}
                    onChange={(e) => type(e.target.value)}
                    className="asa-num"
                    {...props}
                  />
                )}
              </Field>

              {/* The three usual percentages, as one press each.

                  `aria-pressed` rather than a radio group, and deliberately:
                  these do not SELECT anything — they fill the box beside them,
                  which the reader may then edit to 12.5 without any of the
                  three becoming wrong. Compared against the normalised value,
                  so a typed "10,0" still lights the chip a Russian keyboard
                  produced. */}
              <div className="flex flex-wrap items-center gap-1.5">
                {MARKUP_PRESETS.map((value) => {
                  const chosen = markup.trim().replace(',', '.') === value

                  return (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={chosen}
                      onClick={() => type(value)}
                      className={cn(
                        'asa-num rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                        chosen
                          ? 'border-brand-border bg-brand-soft text-brand'
                          : 'border-border-default text-text-body hover:bg-surface-hover',
                      )}
                    >
                      {value}%
                    </button>
                  )
                })}
              </div>

              {/* Allowed on an empty costing and on a zero-cost one (L7): the
                  markup is STORED, so setting it before the lines exist is a
                  legitimate order, and every line written afterwards takes it.
                  The one local refusal is a percentage the server cannot
                  store; the press in flight is the bare `disabled`. */}
              <Button
                variant="secondary"
                onClick={() => markupMutation.mutate(markup.trim())}
                blockedReason={
                  markupUsable ? undefined : t('quotes.pricing.markupInvalid')
                }
                disabled={markupMutation.isPending}
              >
                {t('quotes.pricing.applyMarkup')}
              </Button>
            </div>

            {/* The record-level refusal, above the guidance and below the
                press. `role="alert"` because it appears in response to an
                action a reader has just taken and may not be looking at — the
                same treatment InvoiceLineEditor gives a refused charge, and the
                same wording source: `describeProblem`, never the problem's own
                `detail`, which is written server-side in English for a log
                (AD-22, AD-27). */}
            {markupFailed ? (
              <p
                role="alert"
                className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
              >
                {t(describeProblem(markupMutation.error).detailKey)}
              </p>
            ) : null}

            {/* Said BEFORE the press rather than in a dialog after it: Apply
                stores the markup on the quote and re-prices every line at it,
                and a line written later follows it. Then, on a costing priced
                before the markup was stored, which lines are off it and what an
                edit and an Apply will do to them (L10) — one paragraph of
                guidance about one button, so the two sit closer than the
                card's own rhythm. */}
            <div className="flex flex-col gap-1.5">
              <p className="text-text-muted text-[12px] leading-snug font-light">
                {t('quotes.pricing.applyMarkupConfirm')}
              </p>
              {offLines > 0 ? (
                <p className="text-attention text-[12px] leading-snug">
                  {t('quotes.pricing.markupMixed', { count: offLines })}
                </p>
              ) : null}
            </div>
          </>
        ) : (
          /* The stored markup, read-only (L18). The exact figure the quote
             holds, which on a costing priced before the markup was stored can
             differ from the effective one above. */
          <dl className="flex flex-col gap-0.5">
            <dt className="asa-eyebrow">{t('quotes.pricing.markupStored')}</dt>
            <dd className="asa-num text-text-heading text-[15px] leading-none">
              {percent(record.markupPercent)}
            </dd>
          </dl>
        )}
      </CardBody>

      {draft ? (
        <CardFooter className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-text-muted text-[12px] leading-snug font-light">
            {nothingToInsert
              ? t('quotes.pricing.noPreset')
              : t('quotes.pricing.presetHint')}
          </p>
          <Button
            variant="ghost"
            iconLeft={ListPlus}
            onClick={() =>
              presetMutation.mutate(
                preset.map((template) => ({
                  description: template.label,
                  /* A zero COST, and no price at all (L6): the label is what
                     the preset carries, the cost is this quote's and is entered
                     against the agent's actual price, and the server prices
                     every line at the quote's stored markup. */
                  buy: '0',
                  quantity: 1,
                })),
              )
            }
            disabled={nothingToInsert || presetMutation.isPending}
          >
            {t('quotes.pricing.preset')}
          </Button>

          {/* The footer's own refusal, on its own line — `w-full` inside the
              wrapping row, so it sits under the pair rather than squeezing the
              sentence and the button into a column. No field variant here:
              this button posts a batch the reader never typed, so there is no
              box for a refusal to sit beside. */}
          {presetMutation.isError ? (
            <p
              role="alert"
              className="bg-negative-soft text-negative w-full rounded-md px-3 py-2 text-[12.5px] leading-snug"
            >
              {t(describeProblem(presetMutation.error).detailKey)}
            </p>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  )
}
